import React, { useContext, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
	X,
	Link2,
	Unlink,
	Eye,
	Save,
	Pencil,
	Building,
	AlertTriangle,
	Ban,
	ChevronRight,
	MapPin,
	Move,
	RectangleHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';

import { useCesium } from '../../context/CesiumContext';
import { DeleteButton } from '@/components/DeleteButton';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import PolygonDrawer from '@/helper/PolygonDrawer';
import PointDrawer from '@/helper/PointDrawer';
import { useAssetReadOnly } from '@/context/AssetReadOnlyContext';
import { usePolygonMutations } from '@/hooks/use-polygon-mutations';

import type { DamagePolygonSuccessResponseType } from '@/common/damage-polygon';
import { getDamagePolygonById } from '@/queries/damage-polygons/get-damage-polygon';

type DamagePopupProps = {
	onClose: () => void;
};

export const DamagePopup: React.FC<DamagePopupProps> = ({ onClose }) => {
	const { t } = useTranslation();
	// --- Contexts ---
	const {
		viewer,
		selectedDamageEntity,
		selectedDamage,
		activeDamageContext,
		drawMode,
		setDrawMode,
	} = useCesium();

	const navigate = useNavigate();

	// --- Refs & State ---
	const drawerRef = useRef<PolygonDrawer | PointDrawer | null>(null);
	const editedCoordsRef = useRef<any>(null);
	const [isEditingGeo, setIsEditingGeo] = useState(false);
	// Read-only (inactive organization): the popup only shows the shape, it offers no edits
	const { isReadOnly } = useAssetReadOnly();

	const [fetchedData, setFetchedData] = useState<DamagePolygonSuccessResponseType | null>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [damageHasPolygon, setDamageHasPolygon] = useState(false);
	const [statusChanged, setStatusChanged] = useState(false);

	// --- URL Params ---
	const {
		damageId: urlDamageId,
		inspectionId: urlInspectionId,
		assetId: urlAssetId,
	} = useParams<{
		damageId: string;
		inspectionId: string;
		assetId: string;
	}>();

	const isNewDamage = urlDamageId === 'new';

	// DETERMINE MODE & TYPE
	const properties = selectedDamageEntity?.properties;
	const polygonDbId = selectedDamageEntity?.id;
	const isNewPolygon = !polygonDbId;

	// Detect if the selected entity is a Point or a Polygon
	const isPoint =
		selectedDamageEntity?.type === 'DamagePoint' ||
		selectedDamageEntity?.billboard !== undefined ||
		(Array.isArray(selectedDamage) && typeof selectedDamage[0] === 'number');

	// --- FETCH DATA EFFECT ---
	useEffect(() => {
		const loadPolygonData = async () => {
			if (polygonDbId) {
				setIsFetching(true);
				try {
					const data = await getDamagePolygonById(polygonDbId);
					if (data) {
						setFetchedData(data);
					}
				} catch (error) {
					console.error('Failed to fetch geometry details', error);
					toast.error(t('damages.popup.loadError'));
				} finally {
					setIsFetching(false);
				}
			} else {
				setFetchedData(null);
			}
		};

		loadPolygonData();
	}, [polygonDbId]);

	useEffect(() => {
		if (activeDamageContext?.hasPolygon) {
			setDamageHasPolygon(true);
		} else {
			setDamageHasPolygon(false);
		}
	}, [activeDamageContext]);

	// --- RESOLVE DATA ---
	const activeAssetId = fetchedData?.assetId || properties?.assetId?.getValue() || urlAssetId;
	const activeInspectionId =
		fetchedData?.inspectionId || properties?.inspectionId?.getValue() || urlInspectionId;
	const activeDamageId = isNewPolygon
		? urlDamageId !== 'new'
			? urlDamageId
			: null
		: properties?.damageId?.getValue();
	const polygonDamageId = properties?.damageId?.getValue();
	const inspectionTitle = fetchedData?.inspection?.title || t('damages.popup.inspectionFallback');
	const damageTitle = fetchedData?.damage?.title || t('damages.popup.damageRecordFallback');

	const {
		createMutation,
		updateMutation,
		deleteMutation,
		linkPolygon,
		unlinkPolygon,
		isSubmitting,
	} = usePolygonMutations({
		assetId: activeAssetId,
		inspectionId: activeInspectionId,
		damageId: activeDamageId || '',
		onCreateSuccess: () => onClose(),
		onLinkSuccess: () => onClose(),
		onUnlinkSuccess: () => onClose(),
		onDeleteSuccess: () => onClose(),
	});

	// --- INIT LOGIC ---
	useEffect(() => {
		if (isNewPolygon && selectedDamage) {
			editedCoordsRef.current = selectedDamage;
		}
	}, [isNewPolygon, selectedDamage]);

	useEffect(() => {
		if (drawMode === 'CANCEL') stopDrawing();
	}, [drawMode]);

	// --- NAVIGATION HANDLERS ---
	const goToInspection = () => {
		if (activeAssetId && activeInspectionId) {
			navigate(`/assets/${activeAssetId}/inspections/${activeInspectionId}`);
		}
	};

	const goToDamage = () => {
		if (activeAssetId && activeInspectionId && activeDamageId) {
			navigate(
				`/assets/${activeAssetId}/inspections/${activeInspectionId}/damages/${activeDamageId}`
			);
		}
	};

	// --- ACTION HANDLERS ---
	const handleLink = () => {
		if (polygonDbId && urlDamageId && urlDamageId !== 'new') {
			setStatusChanged(true);
			linkPolygon(polygonDbId, urlDamageId);
		}
	};

	const handleUnlink = () => {
		if (polygonDbId) {
			setStatusChanged(true);
			unlinkPolygon(polygonDbId);
		}
	};

	const startDrawing = () => {
		setIsEditingGeo(true);
		if (drawMode === 'NONE' || drawMode === 'CANCEL') setDrawMode('EDIT');

		if (!drawerRef.current) {
			if (isPoint) {
				// Initialize PointDrawer
				drawerRef.current = new PointDrawer(viewer, {
					once: true,
					tips: { leftClick: t('damages.draw.clickToMovePoint') },
					callback: (coords: number[]) => {
						editedCoordsRef.current = coords;
					},
					posChange: (coords: number[]) => {
						selectedDamageEntity.show = false;
						editedCoordsRef.current = coords;
					},
				});
				drawerRef.current.start();
			} else {
				// Initialize PolygonDrawer
				drawerRef.current = new PolygonDrawer(viewer, {
					callback: (coords: number[][]) => {
						editedCoordsRef.current = coords;
					},
					posChange: (coords: number[][]) => {
						editedCoordsRef.current = coords;
					},
				});

				let coordsToLoad = [];
				if (fetchedData?.polygon?.coordinates) {
					coordsToLoad = fetchedData.polygon.coordinates;
				} else if (selectedDamageEntity?.properties?.rawPositions) {
					coordsToLoad = selectedDamageEntity.properties.rawPositions.getValue();
				} else if (selectedDamage) {
					coordsToLoad = selectedDamage;
				}

				editedCoordsRef.current = coordsToLoad;
				drawerRef.current.loadPolygonFromCoords(coordsToLoad);
			}
		}
	};

	const handleCancelEditing = () => {
		stopDrawing();
		setDrawMode('NONE');
	};

	const stopDrawing = () => {
		if (drawerRef.current) {
			drawerRef.current.clear();
			drawerRef.current.stop();
			drawerRef.current = null;
		}

		if (selectedDamageEntity) {
			selectedDamageEntity.show = true;
		}

		setIsEditingGeo(false);
	};

	const handleSave = () => {
		const finalCoords = editedCoordsRef.current || selectedDamage;

		if (isPoint) {
			if (!finalCoords || finalCoords.length < 2) {
				toast.error(t('damages.popup.invalidPoint'));
				return;
			}
		} else {
			if (!finalCoords || finalCoords.length < 3) {
				toast.error(t('damages.popup.invalidPolygon'));
				return;
			}
		}

		const polygonPayload = {
			type: isPoint ? 'Point' : 'Polygon',
			coordinates: finalCoords,
		};

		if (isNewPolygon) {
			if (!activeAssetId) {
				toast.error(t('damages.popup.missingAssetInfo'));
				return;
			}
			setStatusChanged(true);

			createMutation.mutate({
				assetId: activeAssetId,
				inspectionId: activeInspectionId,
				damageId: activeDamageId,
				polygon: polygonPayload as any,
			});
		} else {
			if (isEditingGeo) {
				setStatusChanged(true);

				updateMutation.mutate(
					{ id: polygonDbId, data: { polygon: polygonPayload as any } },
					{ onSuccess: () => handleClose(), onError: () => stopDrawing() }
				);
			} else {
				handleClose();
			}
		}
	};

	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const handleDelete = () => {
		if (polygonDbId) {
			deleteMutation.mutate(polygonDbId);
		}
	};

	const handleClose = () => {
		// Ensure drawing stops and visibility returns if we close abruptly
		stopDrawing();

		if (viewer) {
			viewer.selectedEntity = undefined;
		}
		onClose();
	};

	const canLinkToOpen = !!!activeDamageId && urlDamageId && urlDamageId !== 'new';
	const isLoading = isSubmitting || isFetching;

	return (
		<div className="absolute top-24 right-6 z-50 w-80 animate-in fade-in slide-in-from-right-4 duration-300">
			{/* py-0 gap-0: the header, content and footer bring their own padding; Card's default
			    24 px above, below and between them left large gaps around the title and the footer. */}
			<Card className="py-0 gap-0 shadow-2xl border-border bg-card/95 backdrop-blur-sm overflow-hidden">
				{/* HEADER */}
				<div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
					<div>
						<h3 className="font-bold text-card-foreground text-sm flex items-center gap-2">
							{isPoint ? (
								<MapPin className="w-4 h-4 text-primary" />
							) : (
								<RectangleHorizontal className="w-4 h-4 text-warning" />
							)}
							{isNewPolygon
								? isPoint
									? t('damages.popup.newDamagePoint')
									: t('damages.popup.newDamagePolygon')
								: isPoint
									? t('damages.popup.damagePoint')
									: t('damages.popup.damagePolygon')}
						</h3>
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
						onClick={handleClose}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>

				<CardContent className="p-4 space-y-5">
					{isFetching && (
						<div className="text-xs text-primary font-medium animate-pulse">
							{t('damages.popup.loadingDetails')}
						</div>
					)}

					{/* EDIT GEOMETRY TOGGLE */}
					{!isReadOnly && drawMode !== 'INSERT' && (
						<>
							{!isEditingGeo ? (
								<div
									onClick={startDrawing}
									className="flex items-center justify-between text-foreground cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors border border-transparent hover:border-border group"
								>
									<div className="flex items-center gap-2">
										{isPoint ? (
											<Move className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
										) : (
											<Pencil className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
										)}
										<span className="text-sm font-medium">
											{isPoint ? t('damages.popup.movePoint') : t('damages.popup.editShape')}
										</span>
									</div>
									<ChevronRight className="h-4 w-4 text-muted-foreground/50" />
								</div>
							) : (
								<div
									onClick={handleCancelEditing}
									className="flex items-center justify-center gap-2 text-warning font-semibold cursor-pointer bg-warning/10 hover:bg-warning/20 p-2 rounded-md border border-warning/30 transition-colors"
								>
									<Ban className="h-4 w-4" />
									<span className="text-sm">{t('damages.popup.cancelEditing')}</span>
								</div>
							)}
						</>
					)}

					<Separator className="bg-foreground/10" />

					{/* INSPECTION DETAILS */}
					<div className="space-y-1.5">
						<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
							<Building className="h-3 w-3" />
							{t('damages.popup.inspectionLabel')}
						</div>
						<div className="flex items-center justify-between bg-muted/30 border border-foreground/20 p-2 rounded-md">
							<div className="min-w-0 flex-1 mr-2">
								<div
									className="text-sm font-semibold text-foreground truncate"
									title={inspectionTitle}
								>
									{inspectionTitle}
								</div>
							</div>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7 text-primary hover:bg-primary/10 hover:text-primary"
								onClick={goToInspection}
								disabled={!activeInspectionId}
								title={t('damages.popup.goToInspection')}
							>
								<Eye className="h-4 w-4" />
							</Button>
						</div>
					</div>

					<Separator className="bg-foreground/10" />

					{/* DAMAGE DETAILS */}
					<div className="space-y-1.5">
						<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
							{polygonDamageId ? <Link2 className="h-3 w-3" /> : <Unlink className="h-3 w-3" />}
							{t('damages.popup.linkedDamageLabel')}
						</div>

						{polygonDamageId ? (
							<div className="flex items-center justify-between bg-muted/30 border border-foreground/20 p-2 rounded-md">
								<div className="min-w-0 flex-1 mr-2">
									<div
										className="text-sm font-semibold text-foreground truncate"
										title={damageTitle}
									>
										{damageTitle}
									</div>
									<div
										className="text-[10px] text-muted-foreground font-mono truncate"
										title={polygonDamageId}
									>
										{t('damages.popup.idLabel', { id: polygonDamageId })}
									</div>
								</div>

								<div className="flex items-center gap-1">
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 text-primary hover:bg-background/50 hover:text-primary"
										onClick={goToDamage}
										title={t('damages.popup.goToDamage')}
									>
										<Eye className="h-4 w-4" />
									</Button>

									{!isReadOnly && !isNewPolygon && (drawMode === 'NONE' || drawMode === 'CANCEL') && (
										<Button
											size="icon"
											variant="ghost"
											className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
											onClick={handleUnlink}
											disabled={isLoading}
											title={t('damages.popup.unlink')}
										>
											<Unlink className="h-4 w-4" />
										</Button>
									)}
								</div>
							</div>
						) : (
							<div className="space-y-3">
								<div className="text-sm text-muted-foreground italic bg-muted/30 px-2 py-2 rounded border border-border flex items-center gap-2">
									<AlertTriangle className="h-3 w-3" />
									{isNewDamage
										? isPoint
											? t('damages.popup.saveToLinkPointHint')
											: t('damages.popup.saveToLinkPolygonHint')
										: t('damages.popup.notLinkedToDamage')}
								</div>

								{!isReadOnly && !isNewPolygon && canLinkToOpen && !damageHasPolygon && (
									<Button
										variant="outline"
										size="sm"
										className="w-full justify-start text-primary border-primary/30 hover:bg-primary/10"
										onClick={handleLink}
										disabled={isLoading}
									>
										<Link2 className="h-3 w-3 mr-2" />
										{t('damages.popup.linkToOpenRecord', { id: urlDamageId })}
									</Button>
								)}

								{!isNewDamage ? (
									<>
										{damageHasPolygon && !statusChanged && (
											<div className="bg-warning/10 border border-warning/30 rounded-md p-3">
												<div className="flex gap-2">
													<AlertTriangle className="h-4 w-4 text-warning shrink-0" />
													<div className="flex-1">
														<h4 className="text-xs font-semibold text-warning">
															{t('damages.popup.cannotLinkTitle')}
														</h4>
														<p className="text-[11px] text-warning/80 mt-0.5 leading-snug">
															{t('damages.popup.cannotLinkDescription', { id: urlDamageId })}
														</p>
													</div>
												</div>
											</div>
										)}
									</>
								) : (
									<></>
								)}
							</div>
						)}
					</div>
				</CardContent>

				{!isNewDamage && (
					<CardFooter className="p-3 bg-muted/30 border-t border-border flex gap-2">
						{!isReadOnly && !isNewPolygon && (drawMode === 'NONE' || drawMode === 'CANCEL') && (
							<DeleteButton
								className="flex-1 shadow-sm"
								onClick={() => setConfirmDeleteOpen(true)}
								pending={deleteMutation.isPending}
								disabled={isLoading}
							/>
						)}

						<Button
							variant="default"
							size="sm"
							className={`flex-1 ${isNewPolygon ? 'w-full' : ''} bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm`}
							onClick={handleSave}
							disabled={isLoading || (isNewPolygon && damageHasPolygon)}
						>
							<Save className="h-3 w-3 mr-2" />
							{isNewPolygon
								? t('common.actions.save')
								: isEditingGeo
									? t('damages.popup.saveChanges')
									: t('common.actions.close')}
						</Button>
					</CardFooter>
				)}
			</Card>
			<AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('damages.popup.deleteTitle')}</AlertDialogTitle>
						<AlertDialogDescription>{t('damages.popup.deleteConfirm')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.actions.cancel')}</AlertDialogCancel>
						<AlertDialogAction className={buttonVariants({ variant: 'destructive' })} onClick={handleDelete}>
							{t('common.actions.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
